import { useEffect, useRef, useState } from 'react';

/**
 * Element width, tracked as it changes.
 *
 * D3 charts are drawn imperatively at a fixed pixel size, so they cannot rely
 * on CSS to reflow — the SVG has to be redrawn whenever its container
 * resizes. The Explorer's panels are resizable and the page is responsive, so
 * a chart that measured once would stay the width it happened to mount at.
 *
 * Height stays a caller-supplied constant. The Explorer's sections are
 * auto-height, so a percentage height resolves against a zero-height ancestor
 * and the chart would measure an empty box — the same trap documented on
 * OverviewChart's CHART_HEIGHT.
 */
export function useChartSize<T extends HTMLElement>(): {
  ref: React.RefObject<T | null>;
  width: number;
} {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Measure once up front: ResizeObserver fires on observe in every browser
    // that matters, but not before the first paint in all of them.
    setWidth(el.clientWidth);

    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width ?? 0;
      // Sub-pixel jitter would redraw the chart on every scroll in browsers
      // that report fractional widths.
      setWidth((prev) => (Math.abs(prev - next) > 1 ? next : prev));
    });
    observer.observe(el);

    // The Explorer unmounts a tab's contents when you switch away, so an
    // observer left connected here fires against a detached node.
    return () => observer.disconnect();
  }, []);

  return { ref, width };
}
